const ISRAEL_TZ = 'Asia/Jerusalem';

type WallParts = {
  y: number;
  m: number;
  d: number;
  hours: number;
  minutes: number;
  seconds: number;
  ms: number;
};

const israelPartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ISRAEL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
});

const getIsraelWallParts = (ms: number): WallParts => {
  const parts = israelPartsFormatter.formatToParts(new Date(ms));
  const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);
  // Intl may return hour 24 for midnight in some engines; normalize to 0.
  const hours = get('hour') % 24;
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    hours,
    minutes: get('minute'),
    seconds: get('second'),
    ms: 0
  };
};

const wallKey = (p: Pick<WallParts, 'y' | 'm' | 'd' | 'hours' | 'minutes' | 'seconds'>): number =>
  ((((p.y * 100 + p.m) * 100 + p.d) * 100 + p.hours) * 100 + p.minutes) * 100 + p.seconds;

/**
 * Finds the UTC instant whose Israel wall-clock equals the given Y-M-D HH:mm:ss.
 * Uses binary search so DST transitions don't require offset tables.
 */
const israelWallTimeToUtc = (
  y: number,
  m: number,
  d: number,
  hours: number,
  minutes: number,
  seconds = 0
): Date => {
  let lo = Date.UTC(y, m - 1, d - 1, 0, 0, 0);
  let hi = Date.UTC(y, m - 1, d + 2, 0, 0, 0);
  const target = wallKey({ y, m, d, hours, minutes, seconds });
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (wallKey(getIsraelWallParts(mid)) < target) lo = mid + 1;
    else hi = mid;
  }
  return new Date(lo);
};

export const parseDateValue = (value?: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseTimeParts = (
  timeValue: Date | string
): { hours: number; minutes: number; seconds: number; ms: number } | null => {
  if (typeof timeValue === 'string') {
    const match = timeValue.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        return { hours, minutes, seconds: 0, ms: 0 };
      }
    }
  }

  const timeAsDate = parseDateValue(timeValue);
  if (!timeAsDate) {
    return null;
  }
  const zoned = getIsraelWallParts(timeAsDate.getTime());
  return {
    hours: zoned.hours,
    minutes: zoned.minutes,
    seconds: zoned.seconds,
    ms: zoned.ms
  };
};

/**
 * Combines a calendar date with a wall-clock time in Asia/Jerusalem.
 * Event dates are stored as Israel midnight (often previous-day UTC); applying
 * setHours in the process local TZ (e.g. UTC on the server) shifts the day.
 */
export const combineDateAndTime = (
  dateValue?: Date | string | null,
  timeValue?: Date | string | null
): Date | null => {
  const date = parseDateValue(dateValue);
  if (!date) {
    return null;
  }

  if (!timeValue) {
    return date;
  }

  const parts = parseTimeParts(timeValue);
  if (!parts) {
    return date;
  }

  const { y, m, d } = getIsraelWallParts(date.getTime());
  return israelWallTimeToUtc(y, m, d, parts.hours, parts.minutes, parts.seconds);
};

export type EventDataSchedule = {
  date?: Date | string | null;
  time?: string | null;
};

export const resolveEventDateTime = (
  dateValue?: Date | string | null,
  timeValue?: string | null
): Date | null => combineDateAndTime(dateValue, timeValue);

export const resolveEventDateTimeFromEventData = (
  eventData?: EventDataSchedule | null
): Date | null => resolveEventDateTime(eventData?.date, eventData?.time);
